"use client";

import React, { useMemo, useState } from "react";
import { motion } from "motion/react";
import Link from "next/link";
import Image from "next/image";
import {
  ArrowLeft,
  Calendar,
  Clock,
  Share2,
  ChevronRight,
  User,
  ChevronDown,
  Plus,
  Minus,
} from "lucide-react";
import { FaFacebook, FaTwitter, FaLinkedin, FaWhatsapp } from "react-icons/fa";
import {
  decodeHtml,
  calculateReadingTime,
  cleanHeaderTags,
} from "@/_helper/helper";

interface BlogDetailClientProps {
  blogData: any;
  allBlogs: any[];
}

export default function BlogDetailClient({
  blogData,
  allBlogs,
}: BlogDetailClientProps) {
  const [openFAQIndex, setOpenFAQIndex] = useState<number | null>(null);

  const readingTime = useMemo(() => {
    return blogData
      ? calculateReadingTime(blogData.LongDescription || "")
      : "1 min read";
  }, [blogData]);

  const relatedBlogs = useMemo(() => {
    if (!blogData || !allBlogs) return [];
    return allBlogs.filter((b) => b.BlogURL !== blogData.BlogURL).slice(0, 3);
  }, [blogData, allBlogs]);

  const activeFAQs = useMemo(() => {
    return (
      blogData?.BlogFAQList?.filter((faq: any) => faq.IsActive)?.sort(
        (a: any, b: any) => (a.SequenceNo ?? 0) - (b.SequenceNo ?? 0),
      ) || []
    );
  }, [blogData]);

  if (!blogData) {
    return (
      <div className="min-h-screen pt-40 pb-20 text-center flex flex-col items-center justify-center bg-gray-50 px-4">
        <div className="p-10 bg-white rounded-2xl shadow-xl max-w-lg mx-auto border border-gray-100 font-sans">
          <Calendar className="w-16 h-16 text-orange-500 mx-auto mb-6 opacity-20" />
          <h2 className="text-3xl font-bold text-gray-900 mb-4 font-playfair">
            Post Not Found
          </h2>
          <p className="text-gray-500 mb-8 text-base">
            The blog post you're looking for might have been moved or removed.
          </p>
          <Link
            href="/blogs"
            className="inline-flex items-center gap-3 px-8 py-3 bg-gradient-to-r from-orange-500 to-amber-600 text-white rounded-full font-bold shadow-lg hover:shadow-orange-200 transition-all"
          >
            <ArrowLeft size={20} />
            Back to Blogs
          </Link>
        </div>
      </div>
    );
  }

  return (
    <main className="bg-white text-gray-900 overflow-hidden">
      {/* 1. Cinematic Hero Section */}
      <section className="relative h-[60vh] md:h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <Image
            src={
              blogData.BlogBannerImage || blogData.Image || "/placeholder.jpg"
            }
            alt={blogData.BlogTitle}
            fill
            priority
            className="object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900/50 via-gray-900/0 to-gray-900/0" />
        </div>
        {/* Scroll Indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 animate-bounce opacity-50">
          <div className="w-[1px] h-12 bg-gradient-to-b from-transparent to-white"></div>
        </div>
      </section>

      {/* 2. Main Content Area */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-40 -mt-10 mb-10">
        <div className=" max-w-7xl mx-auto">
          {/* Main Article */}
          <div className="bg-white rounded-3xl p-6 md:p-12 shadow-2xl shadow-gray-200/50 border border-gray-100">
            {/* Breadcrumb - Subtle */}
            <nav className="flex items-center gap-2 text-gray-400 text-[10px] uppercase tracking-widest mb-10 pb-6 border-b border-gray-50">
              <Link
                href="/"
                className="hover:text-orange-500 transition-colors"
              >
                Home
              </Link>
              <ChevronRight size={12} />
              <Link
                href="/blogs"
                className="hover:text-orange-500 transition-colors"
              >
                Blogs
              </Link>
              <ChevronRight size={12} />
              <span className="text-gray-900 font-bold truncate max-w-[200px]">
                {blogData.BlogTitle}
              </span>
            </nav>

            <h1 className="text-3xl md:text-5xl font-bold mb-8 leading-[1.2] font-playfair tracking-tight mb-2">
              {blogData.BlogTitle}
            </h1>

            <div className="flex flex-wrap items-center gap-6 text-gray-800 text-sm md:text-base font-medium mb-10">
              <div className="flex items-center gap-2">
                <Calendar size={18} className="text-orange-500" />
                <span>{blogData.BlogDate}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock size={18} className="text-orange-500" />
                <span>{readingTime}</span>
              </div>
            </div>
            {/* Article Content */}
            <article
              className="blog-content prose prose-lg max-w-none prose-orange prose-headings:font-playfair prose-headings:font-bold prose-headings:text-gray-900 prose-p:text-gray-600 prose-p:leading-relaxed"
              dangerouslySetInnerHTML={{
                __html: cleanHeaderTags(decodeHtml(blogData.LongDescription)),
              }}
            />

            {/* FAQ Section */}
            {activeFAQs.length > 0 && (
              <div className="mt-16 pt-10 border-t border-gray-100">
                <h2 className="text-2xl md:text-3xl font-bold mb-8 font-playfair tracking-tight text-gray-900">
                  FAQ
                </h2>
                <div className="space-y-4">
                  {activeFAQs.map((faq: any, index: number) => {
                    const isOpen = openFAQIndex === index;
                    return (
                      <div
                        key={faq.ID || index}
                        className="border border-gray-100 rounded-2xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-all duration-300"
                      >
                        <button
                          onClick={() => setOpenFAQIndex(isOpen ? null : index)}
                          className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-orange-50/30 transition-colors duration-200 cursor-pointer group"
                        >
                          <span className="font-semibold text-gray-900 text-sm md:text-base pr-4">
                            {faq.Question}
                          </span>
                          <div
                            className={`p-1.5 rounded-full transition-all duration-300 ${
                              isOpen
                                ? "bg-orange-100"
                                : "bg-gray-100 group-hover:bg-orange-100"
                            }`}
                          >
                            {isOpen ? (
                              <Minus className="w-4 h-4 text-orange-600 transition-colors" />
                            ) : (
                              <Plus className="w-4 h-4 text-gray-600 group-hover:text-orange-600 transition-colors" />
                            )}
                          </div>
                        </button>
                        <motion.div
                          initial={false}
                          animate={{
                            height: isOpen ? "auto" : 0,
                            opacity: isOpen ? 1 : 0,
                          }}
                          transition={{ duration: 0.3, ease: "easeInOut" }}
                          className="overflow-hidden"
                        >
                          <div className="px-6 pb-5 text-gray-600 text-sm md:text-base leading-relaxed border-t border-gray-100 pt-3">
                            {faq.Answer}
                          </div>
                        </motion.div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Author Footer Card */}
            {/* <div className="mt-16 pt-10 border-t border-gray-100 flex flex-col md:flex-row items-center gap-8 bg-gray-50/50 p-8 rounded-2xl">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center text-white text-3xl font-bold shadow-lg">
                A
              </div>
              <div className="text-center md:text-left">
                <h4 className="text-xl font-bold text-gray-900 mb-2">
                  Written by Aalpine Team
                </h4>
                <p className="text-gray-500 text-sm leading-relaxed max-w-lg">
                  Bringing you the most insightful travel guides and stories
                  from the heart of Gujarat. Our team explores hidden gems and
                  iconic landmarks to help you plan the perfect visit to the
                  Statue of Unity.
                </p>
              </div>
            </div> */}
          </div>
        </div>
      </div>

      {/* 3. Latest Stories - Related Grid */}
      {relatedBlogs.length > 0 && (
        <section className="bg-gray-50 py-20 mt-10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
              <div className="max-w-2xl">
                <h2 className="text-3xl md:text-5xl font-bold mb-4 font-playfair">
                  Latest <span className="text-orange-600">Blogs</span>
                </h2>
                <p className="text-gray-500">
                  Discover more guides, travel tips, and stories from our blog.
                </p>
              </div>
              <Link
                href="/blogs"
                className="text-orange-600 font-bold flex items-center gap-2 group hover:translate-x-1 transition-transform"
              >
                View All Blogs
                <ChevronRight size={18} />
              </Link>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {relatedBlogs.map((blog, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <Link
                    href={`/blogs/${blog?.BlogURL}`}
                    className="group block bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all h-full border border-gray-100"
                  >
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <Image
                        src={blog?.BlogBannerImage || blog?.Image}
                        alt={blog?.BlogTitle || blog?.Title}
                        fill
                        className="object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-4 text-[10px] text-gray-400 font-bold uppercase tracking-widest mb-3">
                        <span className="text-orange-500">
                          {blog?.BlogDate}
                        </span>
                        <span>•</span>
                        <span>Travel Guide</span>
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 group-hover:text-orange-600 transition-colors line-clamp-2 mb-3">
                        {blog?.BlogTitle || blog?.Title}
                      </h3>
                      <button className="flex items-center gap-2 text-sm font-bold text-gray-900 mt-4 group-hover:text-orange-600 transition-all underline decoration-orange-500/30 underline-offset-4 decoration-2">
                        Read More
                        <ArrowLeft className="rotate-180 w-4 h-4" />
                      </button>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
